import { computed, type ComputedRef } from 'vue';
import { useCustomToast } from '@/shared/lib';
import { SETTINGS_TOAST_MESSAGES } from './settings-messages';
import type { SettingsNameConflict } from './settings-name-conflict';
import type { SettingsListQuery } from './settings-list-query';
import type { SettingsListItem } from '../model/settings.types';

export interface SettingsRejectionSource {
  readonly nameConflict: SettingsNameConflict | null;
  readonly validationMessages: readonly string[];
}

/** The reading side, which every settings section has in the same shape. */
export interface SettingsReadStoreLike<T extends SettingsListItem> extends SettingsRejectionSource {
  readonly items: readonly T[];
  readonly isLoading: boolean;
  readonly readError: string | null;
  fetchAll(query?: SettingsListQuery): Promise<boolean>;
}

export interface SettingsListStoreLike<T extends SettingsListItem>
  extends SettingsReadStoreLike<T> {
  create(names: string[]): Promise<boolean>;
  update(id: number, name: string): Promise<boolean>;
  remove(id: number): Promise<boolean>;
}

export interface SettingsManagementReads<T extends SettingsListItem> {
  items: ComputedRef<readonly T[]>;
  isLoading: ComputedRef<boolean>;
  /** Only what the last read failed with: a refused write must not empty the listing. */
  readError: ComputedRef<string | null>;
  nameConflict: ComputedRef<SettingsNameConflict | null>;
  validationMessages: ComputedRef<readonly string[]>;
  fetchAll(query?: SettingsListQuery): Promise<boolean>;
}

export interface SettingsManagement<T extends SettingsListItem>
  extends SettingsManagementReads<T> {
  add(names: string[]): Promise<boolean>;
  edit(id: number, name: string): Promise<boolean>;
  remove(id: number): Promise<boolean>;
}

/**
 * What every section exposes from its store, whatever its write contract is. Origem do Registro
 * writes a payload and the other four write a batch of names, but they read the same way, so
 * keeping a copy of this per section is how the two sides drift.
 */
export function exposeSettingsManagementReads<T extends SettingsListItem>(
  store: SettingsReadStoreLike<T>
): SettingsManagementReads<T> {
  return {
    items: computed(() => store.items),
    isLoading: computed(() => store.isLoading),
    readError: computed(() => store.readError),
    nameConflict: computed(() => store.nameConflict),
    validationMessages: computed(() => store.validationMessages),
    fetchAll: async (query?: SettingsListQuery) => store.fetchAll(query),
  };
}

export function resolveSettingsRejectionMessage(
  store: SettingsRejectionSource
): string | undefined {
  if (store.nameConflict) {
    return store.nameConflict.message ?? SETTINGS_TOAST_MESSAGES.duplicateError;
  }

  return store.validationMessages.length > 0 ? store.validationMessages.join(' ') : undefined;
}

export async function runWithToast(
  toast: ReturnType<typeof useCustomToast>,
  action: () => Promise<boolean>,
  messages: { success: string; error: string },
  resolveErrorMessage?: () => string | undefined
): Promise<boolean> {
  const success = await action();

  if (success) {
    toast.showSuccessToast({ title: messages.success, type: 'success' });
  } else {
    toast.showErrorToast({ title: resolveErrorMessage?.() ?? messages.error, type: 'error' });
  }

  return success;
}

export function createSettingsManagement<T extends SettingsListItem>(
  useStore: () => SettingsListStoreLike<T>
): () => SettingsManagement<T> {
  return function useSettingsManagement() {
    const store = useStore();
    const toast = useCustomToast();

    const reads = exposeSettingsManagementReads(store);
    const rejectionMessage = () => resolveSettingsRejectionMessage(store);

    async function add(names: string[]): Promise<boolean> {
      return runWithToast(
        toast,
        () => store.create(names),
        { success: SETTINGS_TOAST_MESSAGES.addSuccess, error: SETTINGS_TOAST_MESSAGES.addError },
        rejectionMessage
      );
    }

    async function edit(id: number, name: string): Promise<boolean> {
      return runWithToast(
        toast,
        () => store.update(id, name),
        { success: SETTINGS_TOAST_MESSAGES.editSuccess, error: SETTINGS_TOAST_MESSAGES.editError },
        rejectionMessage
      );
    }

    async function remove(id: number): Promise<boolean> {
      return runWithToast(toast, () => store.remove(id), {
        success: SETTINGS_TOAST_MESSAGES.deleteSuccess,
        error: SETTINGS_TOAST_MESSAGES.deleteError,
      });
    }

    return { ...reads, add, edit, remove };
  };
}
